// What an audiobook says, track by track (`NarrationScript`, `Pronunciation`, SuiteCompile/Narration.swift): the
// narration-script preset's text. The audio itself (speech engines, mastering, M4B) isn't on the web.
import { blockPlainText, BookMatter, charIsLetter, charIsNumber, graphemes, trimAll, trimCFWhitespace, verseLines } from '@writing-suite/core';
import { bylineName, formattedDouble, heading as fillHeading } from './manuscript.ts';
import type { AudioSettings, CompiledManuscript, CompiledSection } from './types.ts';

export type NarrationSegment = { type: 'speech'; text: string; fragmentID: string | null } | { type: 'pause'; seconds: number };
export interface NarrationTrack { id: number; title: string; fragmentID: string | null; segments: NarrationSegment[] }
export interface NarrationScript {
  title: string; author: string | null; narrator: string | null; language: string; tracks: NarrationTrack[]; settings: AudioSettings;
}

export const DEFAULT_AUDIO: AudioSettings = {
  matter: ['title-page', 'dedication', 'epigraph'], closingCredits: true,
  chapterHeading: ['Chapter {numberWords}', '{title}'], partHeading: ['Part {numberWords}', '{title}'],
  afterHeading: 1.5, sceneBreak: 2, paragraph: 0.6, leadIn: 0.75, tail: 2.5,
};

const reEscape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** A pattern matching `s` in any case, letter by letter (JavaScript has no inline `(?i:)` everywhere yet). */
const anyCase = (s: string) => [...s].map((c) => {
  const lower = c.toLowerCase(), upper = c.toUpperCase();
  return lower !== upper && [...lower].length === 1 && [...upper].length === 1 ? `[${reEscape(lower)}${reEscape(upper)}]` : reEscape(c);
}).join('');

/** `Pronunciation`: written → spoken, whole words; lowercase keys match any case, longest names first. */
export class Pronunciation {
  private regex: RegExp | null;
  private exact = new Map<string, string>();
  private caseless = new Map<string, string>();

  constructor(map: Record<string, string>) {
    const entries = Object.entries(map).filter(([k]) => trimCFWhitespace(k) !== '').sort((a, b) => graphemes(b[0]).length - graphemes(a[0]).length);
    for (const [k, v] of entries) {
      const target = k === k.toLowerCase() ? this.caseless : this.exact;
      if (!target.has(k)) target.set(k, v);
    }
    const alternatives = entries.map(([k]) => (k === k.toLowerCase() ? anyCase(k) : reEscape(k)));
    this.regex = alternatives.length ? new RegExp(`(?<![\\p{L}\\p{N}])(?:${alternatives.join('|')})(?![\\p{L}\\p{N}])`, 'gu') : null;
  }

  apply(text: string): string {
    if (!this.regex) return text;
    return text.replace(this.regex, (found) => this.exact.get(found) ?? this.caseless.get(found.toLowerCase()) ?? found);
  }
}

const hasLetterOrNumber = (s: string) => graphemes(s).some((c) => charIsLetter(c) || charIsNumber(c));

/** `NarrationScript.build`. */
export function buildNarration(m: CompiledManuscript): NarrationScript {
  const audio = m.preset.render.audio ?? DEFAULT_AUDIO;
  const say = new Pronunciation(m.audiobook.pronunciations);
  const narrator = trimCFWhitespace(m.audiobook.narrator) === '' ? null : m.audiobook.narrator;
  const spoken = new Set(audio.matter.flatMap((raw) => { const b = BookMatter.from(raw); return b ? [b] : []; }));
  const tracks: NarrationTrack[] = [];
  let current: NarrationTrack | null = null;
  const hasSpeech = (t: NarrationTrack) => t.segments.some((s) => s.type === 'speech');

  const open = (title: string, fragmentID: string | null) => {
    if (current && hasSpeech(current)) tracks.push(current);
    current = { id: tracks.length, title, fragmentID, segments: [] };
  };
  const speak = (text: string, fragmentID: string | null) => {
    const t = say.apply(trimAll(text));
    if (t === '') return;
    if (!current) open(m.title, fragmentID);
    current!.segments.push({ type: 'speech', text: t, fragmentID });
  };
  const pause = (seconds: number) => {
    const segs: NarrationSegment[] | undefined = (current as NarrationTrack | null)?.segments;
    if (!(seconds > 0) || !segs || !segs.length) return;
    const last = segs[segs.length - 1];
    if (last.type === 'pause') segs[segs.length - 1] = { type: 'pause', seconds: Math.max(last.seconds, seconds) };
    else segs.push({ type: 'pause', seconds });
  };
  const blocks = (section: CompiledSection) => {
    for (const block of section.blocks) {
      switch (block.kind.type) {
        case 'footnote': continue;
        case 'sceneBreak': pause(audio.sceneBreak); break;
        case 'listItem': {
          // An item is read as a sentence of its own, without its bullet or number.
          const text = trimCFWhitespace(blockPlainText(block).replaceAll('\n', ' '));
          const chars = graphemes(text);
          const last = chars[chars.length - 1];
          speak(last !== undefined && (charIsLetter(last) || charIsNumber(last)) ? text + '.' : text, section.fragmentID);
          break;
        }
        case 'verse':
          // A line is spoken whole, with a short pause at its end; the stanza's end is a paragraph's.
          for (const line of verseLines(block)) {
            const text = trimCFWhitespace(line.inlines.map((i) => (i.type === 'text' ? i.text : '')).join(''));
            if (text === '') continue;
            speak(text, section.fragmentID);
            pause(audio.paragraph * 0.4);
          }
          break;
        default: {
          const text = blockPlainText(block);
          // A paragraph of ornaments written in by hand is a scene break too.
          if (text !== '' && !hasLetterOrNumber(text)) pause(audio.sceneBreak);
          else speak(text.replaceAll('\n', ' '), section.fragmentID);
        }
      }
    }
  };
  const heading = (lines: string[]) => lines.map((line) => {
    const l = trimCFWhitespace(line);
    const chars = graphemes(l);
    return chars.length && '.!?…'.includes(chars[chars.length - 1]) ? l : l + '.';
  }).join(' ');

  const credits = [m.title + '.', m.author ? `Written by ${bylineName(m.author)}.` : null, narrator !== null ? `Narrated by ${narrator}.` : null]
    .filter((x): x is string => x !== null).join(' ');
  if (spoken.has('title-page')) {
    open('Opening Credits', null);
    speak(credits, null);
  }
  // Without parts or chapters, each top-level scene is a chapter.
  const flat = !m.sections.some((s) => s.role === 'chapter' || s.role === 'part');
  for (const section of m.sections) {
    if (section.matter !== null) {
      const matter = section.matter;
      if (!spoken.has(matter) || matter === 'title-page' || BookMatter.isGenerated(matter) || !section.blocks.length) continue;
      open(section.title, section.fragmentID);
      if (BookMatter.placement(matter) === 'back') { speak(section.title, null); pause(audio.afterHeading); }
      blocks(section);
      continue;
    }
    switch (section.role) {
      case 'front-matter': case 'back-matter': case 'skip':
        continue;
      case 'part': case 'chapter': {
        const lines = fillHeading(section.role === 'part' ? audio.partHeading : audio.chapterHeading, section);
        const joined = lines.join(': ');
        open(joined === '' ? section.title : joined, section.fragmentID);
        speak(heading(lines), section.fragmentID);
        pause(audio.afterHeading);
        break;
      }
      default:
        if (flat && section.role === 'scene' && section.blocks.length) open(section.title === '' ? `Chapter ${tracks.length + 1}` : section.title, section.fragmentID);
        else if (section.followsScene) pause(audio.sceneBreak);
    }
    blocks(section);
  }
  if (audio.closingCredits && (current !== null || tracks.length)) {
    open('Closing Credits', null);
    speak('The End.', null);
    pause(audio.afterHeading);
    speak(credits, null);
  }
  const last = current as NarrationTrack | null;
  if (last && hasSpeech(last)) tracks.push(last);
  // Trailing pauses are the tail's job.
  for (const t of tracks) while (t.segments.length && t.segments[t.segments.length - 1].type === 'pause') t.segments.pop();
  return { title: m.title, author: m.author ? bylineName(m.author) : null, narrator, language: m.language, tracks, settings: audio };
}

/** `NarrationScript.text()`: the script as text, for a human narrator or for checking. */
export function narrationText(s: NarrationScript): string {
  const out = [s.title.toUpperCase()];
  if (s.author !== null) out.push(`by ${s.author}`);
  if (s.narrator !== null) out.push(`Narrated by ${s.narrator}`);
  for (const track of s.tracks) {
    out.push(`\n=== ${track.id + 1}. ${track.title} ===`);
    for (const seg of track.segments) out.push(seg.type === 'speech' ? seg.text : `[pause ${formattedDouble(seg.seconds)}s]`);
  }
  return out.join('\n\n') + '\n';
}
